import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CircleDashed, FileSearch, FileText, Loader2 } from "lucide-react";

import type { PersonDetail } from "@/api/db/people";
import type { TermDetail } from "@/api/db/terms";
import { CustomFieldValues } from "@/api/document/custom-field-values";
import { DocumentFields } from "@/api/document/fields";
import { DocumentPatterns } from "@/api/document/patterns";
import { DocumentRender } from "@/api/document/render";
import { DocumentValueResolver } from "@/api/document/value-resolver";
import { orpc } from "@/client";
import { PagedDocumentPreview } from "@/components/document-editor/paged-preview";
import { useTermDetail } from "@/hooks/use-terms";
import { DocumentosLabels } from "@/lib/labels/documentos";
import { cn } from "@/lib/utils";
import { SignaturePreparation } from "@/routes/_app/pessoa/-utils/signature-preparation";
import type { SignaturePreparationForm } from "@/routes/_app/pessoa/-utils/signature-preparation";
import type { DocumentFieldDefinition, DocumentProfile, SignatureTerm } from "@/types/signatures";

export function SendTermsPreview({
	person,
	terms,
	customValues,
	patternValues,
	ready,
	filledFields,
	totalFields,
	recipientReady,
}: {
	person: PersonDetail;
	terms: SignatureTerm[];
	customValues: Record<string, string | undefined>;
	patternValues: SignaturePreparationForm["patternValues"];
	ready: boolean;
	filledFields: number;
	totalFields: number;
	recipientReady: boolean;
}) {
	const [activeTermId, setActiveTermId] = useState<number>();

	useEffect(() => {
		if (activeTermId !== undefined && terms.some((term) => term.id === activeTermId)) {
			return;
		}

		setActiveTermId(terms[0]?.id);
	}, [activeTermId, terms]);

	const activeTerm = terms.find((term) => term.id === activeTermId);

	if (!ready) {
		return (
			<SendTermsWaiting
				terms={terms}
				filledFields={filledFields}
				totalFields={totalFields}
				recipientReady={recipientReady}
			/>
		);
	}

	return (
		<section
			data-component="signature-preview"
			data-term-id={activeTerm?.id}
			className="flex min-h-96 min-w-0 flex-col overflow-hidden border-t border-border bg-background/70 lg:min-h-0 lg:border-t-0 lg:border-l"
		>
			<div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card px-4 py-3">
				<h3 className="text-sm font-semibold text-foreground">
					{DocumentosLabels.preparation.preview}
				</h3>
			</div>

			{terms.length > 1 && (
				<div className="flex gap-1 overflow-x-auto border-b border-border bg-card px-3 py-2">
					{terms.map((term) => (
						<button
							key={term.id}
							type="button"
							data-slot="preview-tab"
							data-term-id={term.id}
							onClick={() => setActiveTermId(term.id)}
							className={cn(
								"shrink-0 rounded-md px-2.5 py-1.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
								activeTermId === term.id && "bg-primary/10 text-primary",
								activeTermId !== term.id && "text-muted-foreground hover:text-foreground",
							)}
						>
							{term.name}
						</button>
					))}
				</div>
			)}

			{activeTerm === undefined && (
				<div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center text-muted-foreground">
					<FileSearch className="size-8" />
					<p className="max-w-xs text-sm">{DocumentosLabels.preparation.previewEmpty}</p>
				</div>
			)}

			{activeTerm !== undefined && (
				<TermLivePreview
					key={activeTerm.id}
					person={person}
					termId={activeTerm.id}
					customValues={customValues}
					patternValues={patternValues}
				/>
			)}
		</section>
	);
}

function SendTermsWaiting({
	terms,
	filledFields,
	totalFields,
	recipientReady,
}: {
	terms: SignatureTerm[];
	filledFields: number;
	totalFields: number;
	recipientReady: boolean;
}) {
	const completed = totalFields === 0 ? 0 : Math.round((filledFields / totalFields) * 100);

	return (
		<section
			data-component="signature-preview"
			className="flex min-h-96 min-w-0 flex-col items-center justify-center overflow-hidden border-t border-border bg-background/60 p-6 lg:min-h-0 lg:border-t-0 lg:border-l"
		>
			<div className="flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary">
				<CircleDashed className="size-6" />
			</div>
			<h3 className="type-section-title mt-4 text-foreground">
				{DocumentosLabels.preparation.documentsWaiting}
			</h3>
			<p className="mt-1 text-sm text-muted-foreground">
				{terms.length === 0 && DocumentosLabels.preparation.noSelection}
				{terms.length > 0 && DocumentosLabels.preparation.fieldProgress(filledFields, totalFields)}
			</p>

			{terms.length > 0 && (
				<div className="mt-5 h-2 w-64 overflow-hidden rounded-full bg-border">
					<div
						className="h-full rounded-full bg-primary transition-[width]"
						style={{ width: `${completed}%` }}
					/>
				</div>
			)}

			<div className="mt-8 grid w-full max-w-2xl gap-3 sm:grid-cols-2">
				<div className="surface-panel p-4">
					<p className="text-sm font-medium text-foreground">
						{DocumentosLabels.preparation.recipient}
					</p>
					<p className="mt-1 text-xs text-muted-foreground">
						{recipientReady && DocumentosLabels.preparation.recipientReady}
						{!recipientReady && DocumentosLabels.preparation.recipientBlocked}
					</p>
				</div>
				<div className="surface-panel p-4">
					<p className="text-sm font-medium text-foreground">{DocumentosLabels.sendFields}</p>
					<p className="mt-1 text-xs text-muted-foreground">
						{DocumentosLabels.preparation.fieldProgress(filledFields, totalFields)}
					</p>
				</div>
			</div>

			{terms.length > 0 && (
				<div className="surface-panel mt-6 w-full max-w-2xl p-4">
					<p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
						{DocumentosLabels.preparation.selectedTerms}
					</p>
					{terms.map((term) => (
						<div key={term.id} className="mt-3 flex items-center gap-2 text-sm text-foreground">
							<FileText className="size-4 text-primary" />
							{term.name}
						</div>
					))}
				</div>
			)}
		</section>
	);
}

function TermLivePreview({
	person,
	termId,
	customValues,
	patternValues,
}: {
	person: PersonDetail;
	termId: number;
	customValues: Record<string, string | undefined>;
	patternValues: SignaturePreparationForm["patternValues"];
}) {
	const term = useTermDetail(termId);
	const globals = useQuery(orpc.documents.fields.list.queryOptions());
	const profile = useQuery(orpc.documents.profile.list.queryOptions());

	if (term.isError || globals.isError || profile.isError) {
		return (
			<p className="p-5 text-sm text-destructive">
				{DocumentosLabels.preparation.previewUnavailable}
			</p>
		);
	}

	if (term.data === undefined || globals.data === undefined || profile.data === undefined) {
		return (
			<div className="flex flex-1 items-center justify-center">
				<Loader2 className="size-5 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (term.data.pages === null) {
		return (
			<p className="p-5 text-sm text-destructive">
				{DocumentosLabels.preparation.previewUnavailable}
			</p>
		);
	}

	return (
		<TermPreviewDocument
			person={person}
			term={term.data}
			pageIds={term.data.pages.map((page) => page.attrs.id)}
			globals={globals.data}
			profile={profile.data}
			customValues={customValues}
			patternValues={patternValues}
		/>
	);
}

function TermPreviewDocument({
	person,
	term,
	pageIds,
	globals,
	profile,
	customValues,
	patternValues,
}: {
	person: PersonDetail;
	term: TermDetail;
	pageIds: string[];
	globals: DocumentFieldDefinition[];
	profile: DocumentProfile;
	customValues: Record<string, string | undefined>;
	patternValues: SignaturePreparationForm["patternValues"];
}) {
	const previewDate = useMemo(() => new Date(), []);
	const html = useMemo(() => {
		const rawPatterns = new Map(
			term.patterns.map((pattern) => [
				pattern.id,
				SignaturePreparation.items(
					patternValues,
					SignaturePreparation.patternKey(term.id, pattern.id),
				).map((item) => ({
					id: item.itemId,
					values: new Map(
						term.customFields
							.filter((field) => field.pattern_id === pattern.id && field.source === "input")
							.flatMap((field) => {
								const canonical = CustomFieldValues.canonical(
									field.input_type,
									item.values[String(field.id)] ?? "",
								);

								if (canonical === null) {
									return [];
								}

								return [[field.id, canonical] as const];
							}),
					),
				})),
			]),
		);
		const inputValues = new Map(
			term.customFields
				.filter((field) => field.pattern_id === null && field.source === "input")
				.flatMap((field) => {
					const canonical = CustomFieldValues.canonical(
						field.input_type,
						customValues[SignaturePreparation.fieldKey(term.id, field.id)] ?? "",
					);

					if (canonical === null) {
						return [];
					}

					return [[field.id, canonical] as const];
				}),
		);
		const fieldMap = new Map([...globals, ...term.customFields].map((field) => [field.id, field]));
		const resolver = new DocumentValueResolver({
			fields: fieldMap,
			values: inputValues,
			patterns: rawPatterns,
			profile: new Map(profile.map((row) => [row.key, row.value])),
			referenced: DocumentFields.referenced(term.blocks),
			mode: "preview",
		});
		const blocks = DocumentPatterns.materialize({
			blocks: term.blocks,
			fields: fieldMap,
			values: resolver.patternValues(),
			mode: "preview",
		});

		return DocumentRender.termFlow({
			blocks,
			fields: fieldMap,
			context: DocumentFields.context(
				DocumentFields.person(person),
				previewDate,
				resolver.globalValues(),
			),
			mode: "preview",
			imageSrc: (file) => `/terms/image/${file}`,
		});
	}, [customValues, globals, patternValues, person, previewDate, profile, term]);

	return (
		<PagedDocumentPreview
			html={html}
			floats={term.floats}
			pageIds={pageIds}
			margins={term.margins}
			typography={term.typography}
		/>
	);
}
